// @flow

// Modules
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

// Styles
import { BLUE_GREY, COOL_GREY, getGameTypeColorFont } from 'src/styles/palettes';

// Methods
import Formatter from 'src/methods/formatter';
import { __ } from 'src/methods/l10n';

/**
 * Styles
 */

const InfosPlayerRoot = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 4% 3%;
  padding-top: 2%;
  margin-bottom: 1em;
  color: white;
  background-color: ${BLUE_GREY[900]};
  border-radius: 8px;
`;

const Title = styled.div`
  display: flex;
  justify-content: center;
  font-size: 1.4em;
  font-weight: 700;
  color: ${(props) => props.color};
`;

const Label = styled.div`
  display: flex;
  margin-top: 1.1em;
  margin-bottom: 5px;
  font-size: 1.1em;
  font-weight: 700;
`;

const Headers = styled.div`
  display: flex;
  padding: 5px 10px;
  color: ${COOL_GREY[200]};
  font-weight: 300;
`;

const Infos = styled.div`
  display: flex;
  padding: 5px 10px;
  font-weight: 700;
  background-color: ${BLUE_GREY[600]};
  border-radius: 6px;
`;

const Cell = styled.div`
  display: flex;
  flex: 1;
  ${(props) => props.isLast && 'justify-content: flex-end;'}
  ${(props) => props.isPrimary && `color: ${props.color};`}
`;

/**
 * Types
 */

type PropTypes = {
  challenge: Object,
  selectedType: number,
  selectedPlayer: number,
  playersList: ?Array<Object>
};

const NO_PLAYER_LABEL = '-';

/*
 ** Component
 */

const InfosPlayer = (props: PropTypes): React$Element<any> => {
  const { challenge, playersList, selectedType, selectedPlayer } = props;
  const [nextLevel, setNextLevel] = useState(null);
  const [player, setPlayer] = useState(playersList ? playersList[selectedPlayer] : null);
  const types = challenge && challenge.types;
  const actualType = types && types[selectedType];
  const challengeLabel = challenge && challenge.name;
  const typeLabel = actualType && actualType.name;
  const prizepool = actualType && actualType.prizepool;
  const primaryColor = getGameTypeColorFont(challenge && challenge.id);
  const getChallengeInfos = () => {
    if (challengeLabel && typeLabel) {
      return `Challenge ${challengeLabel} ${typeLabel}: Dotation ${Formatter.formatNumber(prizepool / 100, '€')}`;
    } else {
      return 'Challenge';
    }
  };

  useEffect(() => {
    const player = playersList ? playersList[selectedPlayer] : null;
    let nextLevel = null;

    if (playersList && player && player.rank !== 1) {
      for (let i = selectedPlayer; i >= 0; i--) {
        if (playersList[i].prize_amount > player.prize_amount) {
          nextLevel = playersList[i];

          break;
        }
      }
    }

    setNextLevel(nextLevel);
    setPlayer(player);
  }, [playersList, selectedPlayer]);

  const renderLevel = (label: string, player: ?Object) => (
    <React.Fragment>
      <Label>{label}</Label>
      <Headers>
        <Cell>{__('Rank')}</Cell>
        <Cell>{__('Points')}</Cell>
        <Cell isLast>{__('Potential winnings')}</Cell>
      </Headers>
      <Infos>
        <Cell>{player ? Formatter.formatOrdinal(player.rank) : NO_PLAYER_LABEL}</Cell>
        <Cell>{player ? Formatter.formatNumber(player.score) : NO_PLAYER_LABEL}</Cell>
        <Cell color={primaryColor} isPrimary={!!player && !!player.prize_amount} isLast>
          {player ? Formatter.formatNumber(player.prize_amount / 100, '€') : NO_PLAYER_LABEL}
        </Cell>
      </Infos>
    </React.Fragment>
  );

  return (
    <InfosPlayerRoot>
      <Title color={primaryColor}>{player ? player.nickname : NO_PLAYER_LABEL}</Title>
      {renderLevel(getChallengeInfos(), player)}
      {renderLevel(__('Next level'), nextLevel)}
    </InfosPlayerRoot>
  );
};

export default InfosPlayer;
